import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../services/api.service';
import { ClientWorkstation, Client, WorkstationRoleType, UpgradeToken, UpgradeLog } from '../../models/client-management.model';
import { DatabaseMapping } from '../../models/database-mapping.model';

@Component({
  selector: 'app-workstations-management',
  imports: [CommonModule, FormsModule],
  templateUrl: './workstations-management.component.html',
  styleUrl: './workstations-management.component.scss'
})
export class WorkstationsManagementComponent implements OnInit {
  workstations: ClientWorkstation[] = [];
  clients: Client[] = [];
  roleTypes: WorkstationRoleType[] = [];
  databaseMappings: DatabaseMapping[] = [];
  untrackedDatabases: string[] = [];
  availableVersions: string[] = [];
  loading = false;
  versionsLoading = false;
  error: string | null = null;

  // Modal state
  showModal = false;
  showRoleModal = false;
  showLogsModal = false;
  isEditMode = false;
  currentWorkstation: Partial<ClientWorkstation> = {};
  selectedWorkstation: ClientWorkstation | null = null;

  // Upgrade logs
  upgradeLogs: UpgradeLog[] = [];
  logsLoading = false;

  // Offline package modal
  showOfflineModal = false;
  offlineWorkstation: ClientWorkstation | null = null;
  offlineVersions: string[] = [];
  offlineVersionsLoading = false;
  selectedOfflineVersion: string = '';
  offlinePackageBuilding = false;
  offlineError: string | null = null;

  // Complete offline upgrade modal
  showCompleteOfflineModal = false;
  completeOfflineWorkstation: ClientWorkstation | null = null;
  completeOfflineMachineName = '';
  completeOfflineNotes = '';
  completeOfflineLoading = false;

  constructor(private apiService: ApiService) {}

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.loading = true;
    this.error = null;

    Promise.all([
      this.apiService.getWorkstations().toPromise(),
      this.apiService.getClientsManagement().toPromise(),
      this.apiService.getRoleTypes().toPromise(),
      this.apiService.getUntrackedDatabases().toPromise(),
      this.apiService.getDatabaseMappings().toPromise()
    ]).then(([workstations, clients, roleTypes, untrackedDbs, mappings]) => {
      this.workstations = workstations || [];
      this.clients = clients || [];
      this.roleTypes = roleTypes || [];
      this.untrackedDatabases = untrackedDbs || [];
      this.databaseMappings = mappings || [];
      this.loading = false;
    }).catch(err => {
      this.error = 'Failed to load data: ' + (err.error?.error || err.message);
      this.loading = false;
    });
  }

  openCreateModal(): void {
    this.isEditMode = false;
    this.availableVersions = [];
    this.currentWorkstation = {
      name: '',
      description: '',
      isActive: true,
      clientParamID: this.clients.length > 0 ? this.clients[0].clientParamID : 0,
      databaseName: undefined,
      expectedVersion: undefined
    };
    this.showModal = true;
  }

  openEditModal(workstation: ClientWorkstation): void {
    this.isEditMode = true;
    this.availableVersions = [];
    this.currentWorkstation = { ...workstation };
    if (workstation.databaseName) {
      this.loadVersionsForDatabase(workstation.databaseName);
    }
    this.showModal = true;
  }

  onDatabaseChange(dbName: string): void {
    this.currentWorkstation.expectedVersion = undefined;
    this.availableVersions = [];
    if (dbName) {
      this.loadVersionsForDatabase(dbName);
    }
  }

  private loadVersionsForDatabase(dbName: string): void {
    this.versionsLoading = true;
    this.apiService.getVersionsForDatabase(dbName).subscribe({
      next: (versions) => {
        this.availableVersions = versions;
        this.versionsLoading = false;
      },
      error: () => {
        this.availableVersions = [];
        this.versionsLoading = false;
      }
    });
  }

  closeModal(): void {
    this.showModal = false;
    this.currentWorkstation = {};
    this.availableVersions = [];
  }

  saveWorkstation(): void {
    if (!this.currentWorkstation.name?.trim()) {
      alert('Workstation name is required');
      return;
    }

    this.loading = true;

    if (this.isEditMode && this.currentWorkstation.clientWsID) {
      this.apiService.updateWorkstation(this.currentWorkstation.clientWsID, this.currentWorkstation as ClientWorkstation).subscribe({
        next: () => {
          this.loadData();
          this.closeModal();
        },
        error: (err) => {
          alert('Failed to update workstation: ' + (err.error?.error || err.message));
          this.loading = false;
        }
      });
    } else {
      this.apiService.createWorkstation(this.currentWorkstation).subscribe({
        next: () => {
          this.loadData();
          this.closeModal();
        },
        error: (err) => {
          alert('Failed to create workstation: ' + (err.error?.error || err.message));
          this.loading = false;
        }
      });
    }
  }

  generateToken(workstation: ClientWorkstation): void {
    this.apiService.generateUpgradeToken(workstation.clientWsID).subscribe({
      next: (token: UpgradeToken) => {
        workstation.latestToken = token;
      },
      error: (err) => {
        alert('Failed to generate token: ' + (err.error?.error || err.message));
      }
    });
  }

  isTokenActive(token?: UpgradeToken): boolean {
    if (!token) return false;
    return new Date(token.expiresAt) > new Date();
  }

  copyToken(token: string): void {
    navigator.clipboard.writeText(token).then(() => {
      alert('Token copied to clipboard');
    });
  }

  deleteWorkstation(workstation: ClientWorkstation): void {
    if (!confirm(`Are you sure you want to delete workstation "${workstation.name}"?`)) {
      return;
    }

    this.loading = true;
    this.apiService.deleteWorkstation(workstation.clientWsID).subscribe({
      next: () => {
        this.loadData();
      },
      error: (err) => {
        alert('Failed to delete workstation: ' + (err.error?.error || err.message));
        this.loading = false;
      }
    });
  }

  openLogsModal(workstation: ClientWorkstation): void {
    this.selectedWorkstation = workstation;
    this.upgradeLogs = [];
    this.logsLoading = true;
    this.showLogsModal = true;

    this.apiService.getWorkstationUpgradeLogs(workstation.clientWsID).subscribe({
      next: (logs) => {
        this.upgradeLogs = logs;
        this.logsLoading = false;
      },
      error: (err) => {
        this.logsLoading = false;
        this.error = 'Failed to load upgrade logs: ' + (err.error?.error || err.message);
      }
    });
  }

  closeLogsModal(): void {
    this.showLogsModal = false;
    this.selectedWorkstation = null;
    this.upgradeLogs = [];
  }

  openRoleModal(workstation: ClientWorkstation): void {
    this.selectedWorkstation = workstation;
    this.showRoleModal = true;
  }

  closeRoleModal(): void {
    this.showRoleModal = false;
    this.selectedWorkstation = null;
  }

  get filteredRoleTypes(): WorkstationRoleType[] {
    const targetDb = this.selectedWorkstation?.databaseName;
    const sourceDb = this.databaseMappings.find(m => m.targetDatabase === targetDb)?.sourceDatabase;
    if (!sourceDb) return this.roleTypes;
    return this.roleTypes.filter(rt => rt.databaseName === sourceDb || !rt.databaseName);
  }

  hasRole(roleTypeName: string): boolean {
    return this.selectedWorkstation?.roles?.includes(roleTypeName) || false;
  }

  toggleRole(roleType: WorkstationRoleType): void {
    if (!this.selectedWorkstation) return;

    const hasRole = this.hasRole(roleType.roleTypeName);

    if (hasRole) {
      this.apiService.removeRoleFromWorkstation(this.selectedWorkstation.clientWsID, roleType.roleTypeID).subscribe({
        next: () => {
          this.selectedWorkstation!.roles = (this.selectedWorkstation!.roles || []).filter(r => r !== roleType.roleTypeName);
        },
        error: (err) => alert('Failed to remove role: ' + (err.error?.error || err.message))
      });
    } else {
      this.apiService.assignRoleToWorkstation(this.selectedWorkstation.clientWsID, roleType.roleTypeID).subscribe({
        next: () => {
          this.selectedWorkstation!.roles = [...(this.selectedWorkstation!.roles || []), roleType.roleTypeName];
        },
        error: (err) => alert('Failed to assign role: ' + (err.error?.error || err.message))
      });
    }
  }

  // ── Offline Package ────────────────────────────────────────────────────────

  openOfflineModal(workstation: ClientWorkstation): void {
    this.offlineWorkstation = workstation;
    this.offlineVersions = [];
    this.selectedOfflineVersion = '';
    this.offlineError = null;
    this.offlineVersionsLoading = true;
    this.showOfflineModal = true;

    this.apiService.getOfflinePackageVersions(workstation.clientWsID).subscribe({
      next: (versions) => {
        this.offlineVersions = versions;
        this.offlineVersionsLoading = false;
        if (versions.length > 0) {
          this.selectedOfflineVersion = versions[versions.length - 1];
        }
      },
      error: (err) => {
        this.offlineError = 'Failed to load versions: ' + (err.error?.error || err.message);
        this.offlineVersionsLoading = false;
      }
    });
  }

  closeOfflineModal(): void {
    this.showOfflineModal = false;
    this.offlineWorkstation = null;
    this.offlineVersions = [];
    this.selectedOfflineVersion = '';
    this.offlineError = null;
  }

  downloadOfflinePackage(): void {
    if (!this.offlineWorkstation || !this.selectedOfflineVersion) return;

    this.offlinePackageBuilding = true;
    this.offlineError = null;

    this.apiService.downloadOfflinePackage(this.offlineWorkstation.clientWsID, this.selectedOfflineVersion).subscribe({
      next: (blob) => {
        const ws = this.offlineWorkstation!;
        const safeName = ws.name.replace(/\s+/g, '_');
        const fileName = `offline_upgrade_${safeName}_${this.selectedOfflineVersion}.zip`;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(url);

        // Update workstation state in-place
        ws.offlineUpgradePending = true;
        ws.offlineUpgradeTargetVersion = this.selectedOfflineVersion;
        ws.offlinePackageGeneratedAt = new Date().toISOString();

        this.offlinePackageBuilding = false;
        this.closeOfflineModal();
      },
      error: (err) => {
        // responseType:'blob' means error bodies arrive as Blobs — parse them to get the JSON message
        if (err.error instanceof Blob) {
          err.error.text().then((text: string) => {
            try {
              const parsed = JSON.parse(text);
              this.offlineError = 'Failed to build package: ' + (parsed.error || text);
            } catch {
              this.offlineError = 'Failed to build package: ' + text;
            }
          });
        } else {
          this.offlineError = 'Failed to build package: ' + (err.error?.error || err.message);
        }
        this.offlinePackageBuilding = false;
      }
    });
  }

  openCompleteOfflineModal(workstation: ClientWorkstation): void {
    this.completeOfflineWorkstation = workstation;
    this.completeOfflineMachineName = '';
    this.completeOfflineNotes = '';
    this.showCompleteOfflineModal = true;
  }

  closeCompleteOfflineModal(): void {
    this.showCompleteOfflineModal = false;
    this.completeOfflineWorkstation = null;
  }

  confirmCompleteOfflineUpgrade(): void {
    if (!this.completeOfflineWorkstation) return;
    const ws = this.completeOfflineWorkstation;
    const targetVersion = ws.offlineUpgradeTargetVersion;

    if (!targetVersion) {
      alert('No target version found for this offline upgrade.');
      return;
    }

    this.completeOfflineLoading = true;
    this.apiService.completeOfflineUpgrade(ws.clientWsID, {
      achievedVersion: targetVersion,
      machineName: this.completeOfflineMachineName || undefined,
      notes: this.completeOfflineNotes || undefined
    }).subscribe({
      next: () => {
        ws.expectedVersion = targetVersion;
        ws.offlineUpgradePending = false;
        ws.offlineUpgradeTargetVersion = undefined;
        ws.offlinePackageGeneratedAt = undefined;
        ws.isOutdated = ws.latestAvailableVersion
          ? ws.latestAvailableVersion !== targetVersion
          : false;
        this.completeOfflineLoading = false;
        this.closeCompleteOfflineModal();
      },
      error: (err) => {
        alert('Failed to complete offline upgrade: ' + (err.error?.error || err.message));
        this.completeOfflineLoading = false;
      }
    });
  }
}
